import { Fragment, useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Avatar,
  Table,
  NumberInput,
  Divider,
  Loader,
  Center,
  Collapse,
  ActionIcon,
  Box,
  Checkbox,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Edit2, Check, X, ChevronDown, ChevronRight, Clock } from 'react-feather';
import { cohortCompensationApi } from '../../../../services/api/cohortCompensation';
import type {
  CoachCompensationResult,
  CompensationLineItem,
  CompensationAuditEntry,
} from '../../../../types';
import { getAvatarColor, getInitialsFromParts } from '../../../../utils/avatarHelpers';
import { formatCents } from '../../../../utils/formatters';
import {
  groupBreakdownBySessionType,
  sumOneOnOneSubtotalCents,
  type OneOnOneGroupRow,
} from '../../../../utils/compensationBreakdown';
import { getLineItemRateUnit } from '../../../../config/compensationConfig';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const PAYMENT_TRACK_LABELS: Record<string, string> = {
  flat_rate_monthly: 'Monthly Flat Rate',
  attendance_dependent_net_10: 'Attendance-Dependent (Net 10)',
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  system_calculated: { label: 'System Calculated', color: 'blue' },
  confirmed: { label: 'Confirmed', color: 'green' },
  pending: { label: 'Pending', color: 'gray' },
};

const AUDIT_ACTION_LABELS: Record<string, string> = {
  confirmed: 'Confirmed',
  adjustment: 'Adjustment',
  recalculation_reset: 'Reset by Recalculation',
  bulk_confirmed: 'Bulk Confirmed',
  // MED-910 AC22: per-coach exclusion toggle, logged distinctly.
  exclusion: 'Exclusion',
};

/**
 * MED-910: emitted line-item key → exclusion component (mirror of backend
 * lineItemKeyToExclusionComponent). null ⇒ not excludable. Kept in sync with
 * medley-api/constants/compensation.ts.
 */
const LINE_ITEM_TO_EXCLUSION_COMPONENT: Record<string, string | null> = {
  group_session_flat: null,
  huddle: 'huddle',
  immersion: 'immersion',
  orientation: 'orientation',
  wrap_up: 'wrap_up',
  cohort_event: null,
  one_on_one: 'one_on_one',
  two_on_one: 'one_on_one',
  workshop: 'workshop',
  in_person_half_day: 'in_person',
  in_person_full_day: 'in_person',
  coach_debrief: 'coach_debrief',
};

function exclusionComponentFor(itemKey: string): string | null {
  return LINE_ITEM_TO_EXCLUSION_COMPONENT[itemKey] ?? null;
}

/** AC0i/AC0t: line item is a pre-scheduling estimate (rate-card defaults). */
function isEstimated(li: CompensationLineItem): boolean {
  return (li.metadata as { source?: string } | null)?.source === 'estimated';
}

/** AC10/AC13: persisted exclusion + which level it came from. */
function exclusionMeta(li: CompensationLineItem): {
  excluded: boolean;
  source: 'cohort' | 'coach' | null;
} {
  const m = li.metadata as { excluded?: boolean; excluded_source?: 'cohort' | 'coach' } | null;
  return { excluded: m?.excluded === true, source: m?.excluded_source ?? null };
}

interface CoachBreakdownDrawerProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  coachUserId: string | null;
  onUpdated: () => void;
}

export function CoachBreakdownDrawer({
  opened,
  onClose,
  cohortId,
  coachUserId,
  onUpdated,
}: CoachBreakdownDrawerProps) {
  const [result, setResult] = useState<CoachCompensationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [adjustments, setAdjustments] = useState<Map<string, number>>(new Map());
  // MED-910 AC8–AC15: per-coach exclusion set being edited in Adjust mode.
  const [excludedComponents, setExcludedComponents] = useState<string[]>([]);
  const [auditLog, setAuditLog] = useState<CompensationAuditEntry[]>([]);
  const [auditOpened, { toggle: toggleAudit }] = useDisclosure(false);

  const fetchData = useCallback(async () => {
    if (!coachUserId) return;
    setLoading(true);
    try {
      const [calcResult, audit] = await Promise.all([
        cohortCompensationApi.getCoachCalculation(cohortId, coachUserId),
        cohortCompensationApi.getAuditLog(cohortId, coachUserId),
      ]);
      setResult(calcResult);
      setExcludedComponents(calcResult.excluded_components ?? []);
      setAuditLog(audit);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load coach compensation details',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [cohortId, coachUserId]);

  useEffect(() => {
    if (opened && coachUserId) {
      fetchData();
      setEditing(false);
      setAdjustments(new Map());
    }
  }, [opened, coachUserId, fetchData]);

  const handleStartEdit = () => {
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setAdjustments(new Map());
    // Revert any unsaved exclusion toggles to the persisted set.
    setExcludedComponents(result?.excluded_components ?? []);
  };

  // MED-910 AC14: per-coach toggle can only further-restrict; a component
  // already excluded cohort-wide is locked (handled in the row UI). Toggling
  // here adds/removes the component from the per-coach set.
  const toggleCoachExclusion = (component: string) => {
    setExcludedComponents(prev =>
      prev.includes(component) ? prev.filter(c => c !== component) : [...prev, component],
    );
  };

  // MED-910 AC11/AC12: an exclusion-only change is a valid save (no amount
  // edits required). Compare against the persisted set.
  const exclusionsChanged =
    JSON.stringify([...excludedComponents].sort()) !==
    JSON.stringify([...(result?.excluded_components ?? [])].sort());

  const handleAdjust = (lineItemId: string, value: number) => {
    setAdjustments(prev => {
      const next = new Map(prev);
      next.set(lineItemId, value);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!result || !coachUserId) return;
    // MED-724: When the record is already confirmed, "Confirm" semantically
    // means "save my edits and keep status confirmed". Route adjustments
    // through adjust-confirm (which accepts both system_calculated and
    // confirmed states), and skip the backend call entirely when there are
    // no edits to persist — calling plain /confirm on an already-confirmed
    // record is rejected by the backend state machine.
    const isAlreadyConfirmed = result.status === 'confirmed';
    const hasChanges = adjustments.size > 0 || exclusionsChanged;
    // Guard: an already-confirmed record with no edits has nothing to save.
    // Previously this fell through and showed a misleading "Compensation
    // Saved" toast (MED-910 QA — masked the real NumberInput bug).
    if (isAlreadyConfirmed && !hasChanges) {
      notifications.show({
        title: 'No Changes',
        message: 'No edits to save.',
        color: 'gray',
      });
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      // MED-910 AC11/AC12: amount edits AND exclusions save in one action.
      // Route through adjust-confirm when EITHER changed (exclusion-only is
      // a valid save). Plain confirm only when nothing changed and the
      // record isn't already confirmed.
      if (hasChanges) {
        const adjustmentArray = Array.from(adjustments.entries()).map(
          ([line_item_id, adjusted_total_cents]) => ({
            line_item_id,
            adjusted_total_cents,
          }),
        );
        await cohortCompensationApi.adjustAndConfirmCoachResult(
          cohortId,
          coachUserId,
          adjustmentArray,
          excludedComponents,
        );
      } else if (!isAlreadyConfirmed) {
        await cohortCompensationApi.confirmCoachResult(cohortId, coachUserId);
      }

      notifications.show({
        title: isAlreadyConfirmed ? 'Compensation Saved' : 'Compensation Confirmed',
        message: isAlreadyConfirmed
          ? `${result.coach_name}'s compensation has been saved`
          : `${result.coach_name}'s compensation has been confirmed`,
        color: 'green',
      });
      setEditing(false);
      setAdjustments(new Map());
      onUpdated();
      fetchData();
    } catch (err) {
      const message =
        err instanceof Error && err.message ? err.message : 'Failed to save compensation';
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const getEffectiveTotal = (li: CompensationLineItem): number => {
    if (adjustments.has(li.id)) return adjustments.get(li.id)!;
    return li.adjusted_total_cents ?? li.total_cents;
  };

  // Group line items by payment track
  const groupByTrack = (items: CompensationLineItem[]) => {
    const groups: Record<string, CompensationLineItem[]> = {};
    for (const item of items) {
      const track = item.payment_track;
      if (!groups[track]) groups[track] = [];
      groups[track].push(item);
    }
    return groups;
  };

  // MED-671: render an explicit "SDA × sessions × members" formula for 1:1
  // and 2-on-1 line items so coaches can see how the estimated total is derived.
  // Falls back to the standard "qty × rate" rendering for all other items.
  //
  // MED-671 QA: for pre-assignment coaches (no groups yet), we don't know the
  // real member count. Showing "24 × $150.00" implies a specific member count
  // and is misleading. Instead, display the rate per session for per-member
  // attendance-dependent items (1:1, 2-on-1, 360 Debrief) until assignment.
  const renderFormulaCell = (li: CompensationLineItem) => {
    const isPreAssignment = result?.estimation_basis === 'pre_assignment';
    const isPerMemberAddOn =
      li.item_key === 'one_on_one' ||
      li.item_key === 'two_on_one' ||
      li.item_key === 'coach_debrief';

    if (isPreAssignment && isPerMemberAddOn) {
      const metadata = li.metadata as { sda_cents?: number | null };
      const rateCents = metadata.sda_cents ?? li.unit_rate_cents;
      if (rateCents !== null && rateCents !== undefined) {
        return (
          <Text size="xs" c="dimmed">
            {formatCents(rateCents)}/session
          </Text>
        );
      }
    }

    if (li.item_key === 'one_on_one' || li.item_key === 'two_on_one') {
      const metadata = li.metadata as {
        session_count?: number;
        member_count?: number;
        sda_cents?: number | null;
      };
      const sessions = metadata.session_count ?? 0;
      const members = metadata.member_count ?? 0;
      const sda = metadata.sda_cents ?? null;
      if (sessions > 0 && members > 0 && sda !== null) {
        return (
          <Text size="xs" c="dimmed">
            {formatCents(sda)} × {sessions} session{sessions !== 1 ? 's' : ''} ×{' '}
            {members} member{members !== 1 ? 's' : ''}
          </Text>
        );
      }
      if (sessions > 0 && members > 0) {
        return (
          <Text size="xs" c="dimmed">
            {sessions} session{sessions !== 1 ? 's' : ''} × {members} member
            {members !== 1 ? 's' : ''} (mixed durations)
          </Text>
        );
      }
    }
    if (li.quantity !== null && li.unit_rate_cents !== null) {
      // MED-949: non-guaranteed items show the unit rate instead of qty × rate.
      // MED-1383: in the unit the calculation uses (per hour for Orientation).
      if (!li.is_guaranteed) {
        return (
          <Text size="xs" c="dimmed">
            {formatCents(li.unit_rate_cents)} {getLineItemRateUnit(li.item_key)}
          </Text>
        );
      }
      return (
        <Text size="xs" c="dimmed">
          {li.quantity} x {formatCents(li.unit_rate_cents)}
        </Text>
      );
    }
    if (li.quantity !== null) {
      return (
        <Text size="xs" c="dimmed">
          {li.quantity} units
        </Text>
      );
    }
    return null;
  };

  // MED-677: A coach is "finalized and announced" once their contract status
  // reaches `announced` or `signed`. Pre-announcement every line item is
  // labeled "Potential"; post-announcement unassigned items render as "N/A".
  const isAnnounced =
    result?.contract_status === 'announced' || result?.contract_status === 'signed';

  const renderLineItemRow = (li: CompensationLineItem, opts: { indent?: boolean } = {}) => {
    const hasExistingAdjustment = li.adjusted_total_cents !== null;
    const hasPendingAdjustment = adjustments.has(li.id);
    const effectiveTotal = getEffectiveTotal(li);
    const originalTotal = li.total_cents;
    const showStrikethrough = hasExistingAdjustment || hasPendingAdjustment;
    // MED-677: Post-announcement, line items that are not tied to one of the
    // coach's confirmed modalities render as "N/A" to give CMs full visibility
    // of what could apply while making it clear what currently does not.
    // Inline editing is disabled for these rows since there is nothing to adjust.
    const isAssignedToCoach = li.is_assigned_to_coach ?? true;
    const showAsNotApplicable = isAnnounced && !isAssignedToCoach;

    // MED-910: exclusion view-state for this row.
    const component = exclusionComponentFor(li.item_key);
    const persisted = exclusionMeta(li);
    const cohortExcluded = persisted.excluded && persisted.source === 'cohort';
    const coachExcludedNow = component != null && excludedComponents.includes(component);
    const isExcluded = cohortExcluded || coachExcludedNow;
    const estimated = isEstimated(li);

    return (
      <Table.Tr key={li.id}>
        <Table.Td style={opts.indent ? { paddingLeft: 28 } : undefined}>
          <Group gap="xs">
            {/* AC9/AC13: per-coach Include toggle in Adjust mode. Disabled
                (locked) when the component is already excluded cohort-wide. */}
            {editing && component != null && !showAsNotApplicable && (
              <Tooltip
                label="Excluded cohort-wide — change in Configure Compensation"
                disabled={!cohortExcluded}
              >
                <Checkbox
                  size="xs"
                  checked={!isExcluded}
                  disabled={cohortExcluded}
                  onChange={() => toggleCoachExclusion(component)}
                  aria-label={`Include ${li.label} for this coach`}
                />
              </Tooltip>
            )}
            <div>
              <Text
                size="sm"
                c={showAsNotApplicable ? 'dimmed' : undefined}
                td={isExcluded ? 'line-through' : undefined}
              >
                {li.label}
                {/* AC0i/AC0t: estimated (pre-scheduling) indicator. */}
                {estimated && !showAsNotApplicable && (
                  <Text component="span" size="xs" c="dimmed" fs="italic">
                    {' '}
                    (est.)
                  </Text>
                )}
              </Text>
              {/* MED-949: variable items show attendance subtext instead of Potential badge */}
              {!li.is_guaranteed && !showAsNotApplicable && !isExcluded && (
                <Text size="xs" c="dimmed">
                  Subject to attendance/completion
                </Text>
              )}
            </div>
            {/* MED-949: guaranteed items get a green GUARANTEED badge */}
            {li.is_guaranteed && !showAsNotApplicable && !isExcluded && (
              <Badge size="xs" color="green" variant="light">
                Guaranteed
              </Badge>
            )}
            {li.is_rate_overridden && !showAsNotApplicable && !isExcluded && (
              <Badge size="xs" color="orange" variant="light">
                Override
              </Badge>
            )}
            {(hasExistingAdjustment || hasPendingAdjustment) &&
              !showAsNotApplicable &&
              !isExcluded && (
                <Badge size="xs" color="orange" variant="filled">
                  ADJUSTED
                </Badge>
              )}
            {/* AC10/AC13: EXCLUDED badge (cohort-wide variant when applicable). */}
            {isExcluded && (
              <Badge size="xs" color="gray" variant="filled">
                {cohortExcluded ? 'EXCLUDED — COHORT-WIDE' : 'EXCLUDED'}
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td style={{ textAlign: 'right' }}>
          {showAsNotApplicable || isExcluded ? null : renderFormulaCell(li)}
        </Table.Td>
        <Table.Td style={{ textAlign: 'right' }}>
          {showAsNotApplicable ? (
            <Text size="sm" c="dimmed" fs="italic">
              N/A
            </Text>
          ) : isExcluded ? (
            // AC10: original amount struck-through + greyed for audit, $0.00.
            <Group gap={4} justify="flex-end">
              <Text size="sm" td="line-through" c="dimmed">
                {formatCents(originalTotal)}
              </Text>
              <Text size="sm" fw={600}>
                {formatCents(0)}
              </Text>
            </Group>
          ) : editing ? (
            <NumberInput
              size="xs"
              w={120}
              min={0}
              step={100}
              prefix="$"
              decimalScale={2}
              fixedDecimalScale
              value={effectiveTotal / 100}
              onChange={(val) => {
                // Mantine v8: NumberInput.onChange returns string | number.
                // The old `typeof val === 'number'` guard silently dropped
                // every string-typed value, so amount edits never reached
                // the adjustments map and never saved (MED-910 QA).
                const num = typeof val === 'number' ? val : parseFloat(String(val));
                if (Number.isFinite(num)) {
                  handleAdjust(li.id, Math.round(num * 100));
                }
              }}
              styles={{ input: { textAlign: 'right' } }}
            />
          ) : (
            <Group gap={4} justify="flex-end">
              {showStrikethrough && effectiveTotal !== originalTotal && (
                <Text size="sm" td="line-through" c="dimmed">
                  {formatCents(originalTotal)}
                </Text>
              )}
              <Text size="sm" fw={600}>
                {formatCents(effectiveTotal)}
              </Text>
            </Group>
          )}
        </Table.Td>
      </Table.Tr>
    );
  };

  // MED-695: nested "+ Coach Debrief Flat Rate" sub-row beneath the 360
  // Debrief 1:1 row. Indented, non-editable (existing coach_debrief editing
  // flow is preserved via the normal edit path — the input/badge render
  // through renderLineItemRow with indent). We render this as a regular
  // indented line item row with a "+" visual cue in its label.
  const renderNestedCoachDebrief = (li: CompensationLineItem) => {
    const hasExistingAdjustment = li.adjusted_total_cents !== null;
    const hasPendingAdjustment = adjustments.has(li.id);
    const effectiveTotal = getEffectiveTotal(li);
    const originalTotal = li.total_cents;
    const showStrikethrough = hasExistingAdjustment || hasPendingAdjustment;
    const isAssignedToCoach = li.is_assigned_to_coach ?? true;
    const showAsNotApplicable = isAnnounced && !isAssignedToCoach;

    return (
      <Table.Tr key={li.id}>
        <Table.Td style={{ paddingLeft: 44 }}>
          <Group gap="xs">
            <div>
              <Text size="sm" c="dimmed">
                + Coach Debrief Flat Rate
              </Text>
              {!showAsNotApplicable && (
                <Text size="xs" c="dimmed">
                  Subject to attendance/completion
                </Text>
              )}
            </div>
            {(hasExistingAdjustment || hasPendingAdjustment) && !showAsNotApplicable && (
              <Badge size="xs" color="orange" variant="filled">
                ADJUSTED
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td />
        <Table.Td style={{ textAlign: 'right' }}>
          {showAsNotApplicable ? (
            <Text size="sm" c="dimmed" fs="italic">
              N/A
            </Text>
          ) : editing ? (
            <NumberInput
              size="xs"
              w={120}
              min={0}
              step={100}
              prefix="$"
              decimalScale={2}
              fixedDecimalScale
              value={effectiveTotal / 100}
              onChange={(val) => {
                // Mantine v8: NumberInput.onChange returns string | number.
                // The old `typeof val === 'number'` guard silently dropped
                // every string-typed value, so amount edits never reached
                // the adjustments map and never saved (MED-910 QA).
                const num = typeof val === 'number' ? val : parseFloat(String(val));
                if (Number.isFinite(num)) {
                  handleAdjust(li.id, Math.round(num * 100));
                }
              }}
              styles={{ input: { textAlign: 'right' } }}
            />
          ) : (
            <Group gap={4} justify="flex-end">
              {showStrikethrough && effectiveTotal !== originalTotal && (
                <Text size="sm" td="line-through" c="dimmed">
                  {formatCents(originalTotal)}
                </Text>
              )}
              <Text size="sm" fw={600}>
                {formatCents(effectiveTotal)}
              </Text>
            </Group>
          )}
        </Table.Td>
      </Table.Tr>
    );
  };

  // MED-695: Render the grouped "1:1 Coaching (Potential)" section —
  // subsection header row + per-session-type rows (indented) + optional
  // nested Coach Debrief Flat Rate row beneath the 360 Debrief row +
  // "1:1 Coaching Subtotal" row.
  const renderOneOnOneSection = (groups: OneOnOneGroupRow[]) => {
    const subtotalCents = sumOneOnOneSubtotalCents(groups, getEffectiveTotal, {
      excludeUnassigned: isAnnounced,
    });
    // MED-949: dropped "(Potential)" — individual rows now carry their own badge/subtext
    const headerLabel = '1:1 Coaching';
    return (
      <>
        <Table.Tr key="one-on-one-header" style={{ background: '#FAFAF8' }}>
          <Table.Td colSpan={2}>
            <Group gap="xs">
              <Text size="sm" fw={700}>
                {headerLabel}
              </Text>
            </Group>
          </Table.Td>
          <Table.Td style={{ textAlign: 'right' }}>
            <Text size="sm" fw={700}>
              {formatCents(subtotalCents)}
            </Text>
          </Table.Td>
        </Table.Tr>
        {groups.map(g => (
          <Fragment key={g.lineItem.id}>
            {renderLineItemRow(g.lineItem, { indent: true })}
            {g.nestedCoachDebrief && renderNestedCoachDebrief(g.nestedCoachDebrief)}
          </Fragment>
        ))}
        <Table.Tr key="one-on-one-subtotal">
          <Table.Td colSpan={2} style={{ paddingLeft: 28 }}>
            <Text size="xs" c="dimmed" fw={600}>
              1:1 Coaching Subtotal
            </Text>
          </Table.Td>
          <Table.Td style={{ textAlign: 'right' }}>
            <Text size="xs" c="dimmed" fw={700}>
              {formatCents(subtotalCents)}
            </Text>
          </Table.Td>
        </Table.Tr>
      </>
    );
  };

  if (!coachUserId) return null;

  const statusConfig = result ? (STATUS_CONFIG[result.status] ?? STATUS_CONFIG.pending) : null;
  const nameParts = (result?.coach_name ?? '').split(' ');
  const initials = getInitialsFromParts(nameParts[0] ?? '', nameParts.slice(1).join(' ') ?? '');
  const avatarColor = result ? getAvatarColor(result.coach_user_id) : 'gray';

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Coach Compensation Breakdown"
      position="right"
      size="lg"
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      {loading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : !result ? (
        <Text c="dimmed" ta="center" py="xl">
          No compensation data found.
        </Text>
      ) : (
        <Stack gap="md">
          {/* Coach Header */}
          <Group justify="space-between">
            <Group gap="sm">
              <Avatar size="md" radius="xl" color={avatarColor}>
                {initials}
              </Avatar>
              <div>
                <Text fw={600}>{result.coach_name}</Text>
                <Text size="xs" c="dimmed">
                  {result.group_count} group{result.group_count !== 1 ? 's' : ''} · {result.member_count} member{result.member_count !== 1 ? 's' : ''}
                </Text>
              </div>
            </Group>
            {statusConfig && (
              <Badge
                size="lg"
                color={statusConfig.color}
                variant={result.status === 'confirmed' ? 'filled' : 'light'}
              >
                {statusConfig.label}
              </Badge>
            )}
          </Group>

          {/* Confirmation info */}
          {result.status === 'confirmed' && result.confirmed_by_name && result.confirmed_at && (
            <Text size="xs" c="dimmed">
              Confirmed by {result.confirmed_by_name} on {formatDate(result.confirmed_at)}
            </Text>
          )}

          {/* Summary totals */}
          {/* MED-677: Post-announcement the displayed estimated total and
              monthly flat rate must reflect only the line items tied to the
              coach's actual assignments, so the summary card matches the
              per-track subtotals (which also exclude N/A items). The Monthly
              Flat Rate side is suppressed entirely when the coach has no
              assigned flat-rate line items (e.g. a 1:1-only coach on a cohort
              that also has a group coaching flat rate). Pre-announcement we
              continue to show the stored values since every item applies. */}
          {(() => {
            const displayedEstimatedTotal = isAnnounced
              ? result.line_items.reduce(
                  (sum, li) => (li.is_assigned_to_coach ? sum + getEffectiveTotal(li) : sum),
                  0,
                )
              : (result.estimated_total_cents ?? 0);
            const hasAssignedFlatRate = result.line_items.some(
              li =>
                li.payment_track === 'flat_rate_monthly' && (li.is_assigned_to_coach ?? true),
            );
            const showMonthlyFlatRate = isAnnounced
              ? hasAssignedFlatRate && !!result.flat_rate_monthly_cents
              : !!result.flat_rate_monthly_cents;
            const displayedFlatRateMonthly =
              isAnnounced && result.program_months && result.program_months > 0
                ? Math.round(
                    result.line_items.reduce(
                      (sum, li) =>
                        li.payment_track === 'flat_rate_monthly' &&
                        (li.is_assigned_to_coach ?? true)
                          ? sum + getEffectiveTotal(li)
                          : sum,
                      0,
                    ) / result.program_months,
                  )
                : (result.flat_rate_monthly_cents ?? 0);
            return (
              <Box
                p="md"
                style={{ border: '1px solid #E8E6E1', borderRadius: 8, background: '#FAFAF8' }}
              >
                <Group justify="space-between">
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Estimated Total
                    </Text>
                    <Text size="xl" fw={700}>
                      {formatCents(displayedEstimatedTotal)}
                    </Text>
                  </div>
                  {showMonthlyFlatRate && (
                    <div style={{ textAlign: 'right' }}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Monthly Flat Rate
                      </Text>
                      <Text size="lg" fw={600}>
                        {formatCents(displayedFlatRateMonthly)}/mo
                      </Text>
                      {result.program_months && (
                        <Text size="xs" c="dimmed">
                          {result.program_months} month program
                        </Text>
                      )}
                    </div>
                  )}
                </Group>
              </Box>
            );
          })()}

          <Divider />

          {/* Line items grouped by payment track. MED-695: within the
              attendance-dependent track, per-session-type 1:1 rows are
              nested under a "1:1 Coaching (Potential)" sub-section with
              the Coach Debrief flat rate folded under the 360 Debrief row. */}
          {Object.entries(groupByTrack(result.line_items)).map(([track, items]) => {
            const grouped =
              track === 'attendance_dependent_net_10'
                ? groupBreakdownBySessionType(items)
                : null;
            const nonGroupedItems = grouped ? grouped.otherLineItems : items;
            return (
            <Stack key={track} gap="xs">
              <Text size="sm" fw={600} tt="uppercase" c="dimmed">
                {PAYMENT_TRACK_LABELS[track] ?? track}
              </Text>
              <Table
                withTableBorder
                styles={{
                  th: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase' },
                }}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Item</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Formula</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Amount</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {grouped?.hasOneOnOneSection && renderOneOnOneSection(grouped.oneOnOneGroups)}
                  {nonGroupedItems.map(item => renderLineItemRow(item))}
                  {/*
                    MED-671 QA: for the Monthly Flat Rate section, surface the
                    monthly installment breakdown inline so the "Monthly Flat
                    Rate" section name isn't confusing (the subtotal is the
                    program total, not the monthly amount).

                    MED-677: Suppress the installments helper row post-
                    announcement when the coach has no assigned flat-rate line
                    items in this track — otherwise we'd show "$780/mo × 4
                    months" next to a subtotal of $0 and a `group_session_flat`
                    row rendered as N/A.
                  */}
                  {track === 'flat_rate_monthly' &&
                    result.flat_rate_monthly_cents !== null &&
                    result.program_months !== null &&
                    (!isAnnounced ||
                      items.some(li => li.is_assigned_to_coach ?? true)) && (
                      <Table.Tr>
                        <Table.Td>
                          <Text size="sm">Monthly Installments</Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="xs" c="dimmed">
                            {formatCents(result.flat_rate_monthly_cents)}/mo ×{' '}
                            {result.program_months} month
                            {result.program_months !== 1 ? 's' : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td />
                      </Table.Tr>
                    )}
                </Table.Tbody>
                <Table.Tfoot>
                  <Table.Tr>
                    <Table.Td colSpan={2}>
                      <Text size="sm" fw={600}>
                        Subtotal
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="sm" fw={700}>
                        {/* MED-677: Post-announcement, unassigned items render
                            as "N/A" and must not be counted in the subtotal. */}
                        {formatCents(
                          items.reduce((sum, li) => {
                            if (isAnnounced && !(li.is_assigned_to_coach ?? true)) return sum;
                            return sum + getEffectiveTotal(li);
                          }, 0),
                        )}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tfoot>
              </Table>
            </Stack>
            );
          })}

          <Divider />

          {/* Action buttons */}
          <Group justify="flex-end" gap="sm">
            {editing ? (
              <>
                <Button
                  variant="outline"
                  color="gray"
                  leftSection={<X size={16} />}
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  leftSection={<Check size={16} />}
                  onClick={handleConfirm}
                  loading={saving}
                >
                  {adjustments.size > 0 || exclusionsChanged
                    ? 'Save Adjustments'
                    : 'Confirm'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  leftSection={<Edit2 size={16} />}
                  onClick={handleStartEdit}
                >
                  Adjust
                </Button>
                {result.status !== 'confirmed' && (
                  <Button
                    leftSection={<Check size={16} />}
                    onClick={handleConfirm}
                    loading={saving}
                  >
                    Confirm
                  </Button>
                )}
              </>
            )}
          </Group>

          <Divider />

          {/* Audit Trail */}
          <Box>
            <Group
              gap="xs"
              style={{ cursor: 'pointer' }}
              onClick={toggleAudit}
            >
              <ActionIcon variant="subtle" size="sm">
                {auditOpened ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </ActionIcon>
              <Text size="sm" fw={600}>
                Activity Log ({auditLog.length})
              </Text>
            </Group>
            <Collapse in={auditOpened}>
              <Stack gap="xs" mt="sm" pl="md">
                {auditLog.length === 0 ? (
                  <Text size="xs" c="dimmed">
                    No activity recorded yet.
                  </Text>
                ) : (
                  auditLog.map(entry => (
                    <Group key={entry.id} gap="xs" align="flex-start">
                      <Clock size={12} style={{ marginTop: 3, flexShrink: 0 }} />
                      <div>
                        <Text size="xs">
                          <Text span fw={600}>
                            {AUDIT_ACTION_LABELS[entry.action] ?? entry.action}
                          </Text>
                          {' by '}
                          {entry.performed_by_name}
                        </Text>
                        {entry.action === 'adjustment' && entry.details && (
                          <Text size="xs" c="dimmed">
                            {(entry.details as Record<string, unknown>).label as string}:{' '}
                            {formatCents((entry.details as Record<string, unknown>).original_total_cents as number)}
                            {' → '}
                            {formatCents((entry.details as Record<string, unknown>).adjusted_total_cents as number)}
                          </Text>
                        )}
                        <Text size="xs" c="dimmed">
                          {formatDate(entry.created_at)}
                        </Text>
                      </div>
                    </Group>
                  ))
                )}
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      )}
    </Drawer>
  );
}
